import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FiSearch } from "react-icons/fi";
import { Badge, Spinner } from "@/components/ui";
import { SECTION_LABELS } from "@/config/constants";
import { getStrapiMedia } from "@/lib/strapi";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { ROUTES } from "@/routes/paths";
import { useExploreStore, sectionFilter } from "@/features/explore";
import { useProducts } from "../hooks/useProducts";

export function ProductsPage() {
  const section = useExploreStore((s) => s.section);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const debouncedSearch = useDebouncedValue(search.trim());

  // Category options for the current section (independent of search/category).
  const { data: catData } = useProducts({
    fields: ["category"],
    filters: sectionFilter(section),
    pagination: { pageSize: 100 },
  });
  const categories = useMemo(
    () =>
      [...new Set((catData?.items ?? []).map((p) => p.category).filter(Boolean))].sort(),
    [catData]
  );

  const filters = {
    ...sectionFilter(section),
    ...(debouncedSearch ? { title: { $containsi: debouncedSearch } } : {}),
    ...(category ? { category: { $eq: category } } : {}),
  };

  const { data, isLoading, error } = useProducts({ populate: "*", filters });
  const products = data?.items ?? [];

  return (
    <div>
      <h1 className="mb-6 text-2xl font-bold text-foreground">Products</h1>

      <div className="mb-6 flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <FiSearch
            size={18}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search products…"
            className="w-full rounded-md border border-input bg-background py-2 pl-9 pr-3 text-foreground focus:border-ring focus:ring-1 focus:ring-ring"
          />
        </div>
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="rounded-md border border-input bg-background p-2 text-foreground sm:w-56"
        >
          <option value="">All categories</option>
          {categories.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      ) : error ? (
        <p className="text-destructive">Failed to load products.</p>
      ) : products.length === 0 ? (
        <p className="text-muted-foreground">No products match your search.</p>
      ) : (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {products.map((p) => {
            const img = getStrapiMedia(p.images?.[0]);
            return (
              <Link
                key={p.documentId}
                to={ROUTES.PRODUCT_DETAIL(p.documentId)}
                className="overflow-hidden rounded-lg border border-border bg-card transition-colors hover:border-primary"
              >
                <div className="aspect-square bg-secondary">
                  {img && (
                    <img src={img} alt={p.title} className="h-full w-full object-cover" />
                  )}
                </div>
                <div className="p-4">
                  <div className="mb-1 flex items-start justify-between gap-2">
                    <p className="truncate font-medium text-foreground">{p.title}</p>
                    {p.section && (
                      <Badge variant="primary">
                        {SECTION_LABELS[p.section] ?? p.section}
                      </Badge>
                    )}
                  </div>
                  <p className="text-lg font-medium text-accent">${p.price}</p>
                  {p.designer?.name && (
                    <p className="mt-1 truncate text-sm text-muted-foreground">
                      by {p.designer.name}
                    </p>
                  )}
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default ProductsPage;
